import { Link } from "react-router";
import { breadcrumbJsonLd, buildMeta } from "../lib/seo.js";
import Breadcrumbs from "../components/Breadcrumbs/Breadcrumbs.jsx";
import Picture from "../components/Picture/Picture.jsx";
import VideoReel from "../components/VideoReel/VideoReel.jsx";
import SectionHeader from "../components/SectionHeader/SectionHeader.jsx";
import CTABand from "../components/CTABand/CTABand.jsx";
import { surfaceFor } from "../lib/surface.js";
import styles from "./bim-photorealism.module.css";

const PAGE = {
  title: "Photorealism",
  description:
    "Ray-traced, photorealistic stills and animations of your design, for public meetings, hearings and stakeholder review.",
  path: "/123bim/photorealism",
};
const CRUMBS = [
  { name: "Home", path: "/" },
  { name: "3D Visualization", path: "/3d-visualization" },
  { name: "Photorealism", path: PAGE.path },
];

export function meta() {
  return buildMeta({
    ...PAGE,
    image: "/images/site/123bim/photo-brunswick",
    jsonLd: [breadcrumbJsonLd(CRUMBS)],
  });
}

// The one switch for this page's light sections: these sit on the light "plan sheet"
// surface from app/styles/surfaces.css. Empty the list to put the whole page back on the
// dark surface; nothing else needs to change.
const LIGHT_SECTIONS = ["staging"];
const surface = surfaceFor(LIGHT_SECTIONS);

const GRID_VIDEOS = [419792308, 419792477, 427330988, 428070028, 446945299];

// What the same ray-traced scene can be rendered under.
const CONDITIONS = ["Sun angle", "Season", "Water levels", "Fog", "Shadow"];

export default function BimPhotorealism() {
  return (
    <>
      {/* The page is about the pictures, so a picture leads it, the whole width of the screen. */}
      <section className={styles.hero} aria-labelledby="photo-title">
        <Picture
          src="/images/site/123bim/photo-brunswick"
          alt="Photorealistic rendering of the riverfront in Brunswick, Maine: a mill building on the water, the bridge beyond and the town on the far bank"
          ratio="16 / 9"
          widths={[960, 1600]}
          fallbackWidth={1600}
          sizes="100vw"
          priority
          className={styles.heroImage}
        />
        <div className={styles.heroShade} aria-hidden="true" />
        <div className={`container ${styles.heroText}`}>
          <Breadcrumbs items={CRUMBS} />
          <h1 id="photo-title" className={styles.heroTitle}>
            Photorealistic renderings, from sketch to finished design
          </h1>
          <p className={styles.heroLead}>
            In addition to the photorealism built into 3D Visualization, RDV Systems is a
            full-service visualization studio: high-resolution renderings of existing conditions,
            final design and everything in between, from a napkin sketch to the finished project.
          </p>
          <div className={styles.heroActions}>
            <Link to="/demo" className="btn btn-primary">
              Request a demo
            </Link>
            <a href="#videos" className={styles.heroLink}>
              See it in the field
            </a>
          </div>
        </div>
      </section>

      <section className="section" aria-labelledby="daylight-title">
        <div className={`container ${styles.split}`}>
          <Picture
            src="/images/site/123bim/photo-morning"
            alt="A ray-traced morning rendering of a redesigned bridge, traffic crossing between white arches in low sun"
            ratio="16 / 9"
            widths={[480, 960, 1600]}
            fallbackWidth={960}
            sizes="(min-width: 64rem) 55vw, 100vw"
            className={styles.splitImage}
          />
          <div className={styles.splitText}>
            <h2 id="daylight-title">Time of day and season</h2>
            <p>
              On-demand photorealistic rendering comes back in a fraction of the time a traditional
              studio needs, and the same raytraced model renders under different environmental
              factors.
            </p>
            <ul className={styles.conditions} aria-label="Conditions a scene can be rendered under">
              {CONDITIONS.map((c) => (
                <li key={c}>{c}</li>
              ))}
            </ul>
            <p>
              A morning raytrace of a redesigned bridge or street shows stakeholders the project the
              way they will actually experience it, not a flat daytime rendering that hides how the
              design reads at rush hour or in winter light.
            </p>
          </div>
        </div>
      </section>

      <section
        className="section"
        data-surface={surface("staging")}
        aria-labelledby="staging-title"
      >
        <div className={`container ${styles.split} ${styles.reverse}`}>
          <Picture
            src="/images/site/123bim/photo-stage"
            alt="A photorealistic rendering of a construction staging phase at the same crossing: temporary trusses, traffic cones and traffic on the deck"
            ratio="16 / 9"
            widths={[480, 960, 1600]}
            fallbackWidth={960}
            sizes="(min-width: 64rem) 55vw, 100vw"
            className={styles.splitImage}
          />
          <div className={styles.splitText}>
            <h2 id="staging-title">Construction staging</h2>
            <p>
              Layer controls show construction staging and information overlays on the same
              photorealistic model used for the finished design, so a public meeting can see each
              phase of the work instead of only the end state.
            </p>
            <p>
              That staging view is rendered from the identical raytraced scene as the final image,
              so there is no separate model to keep in sync as the construction sequence changes.
            </p>
          </div>
        </div>
      </section>

      <section className="section" id="videos" aria-labelledby="videos-title">
        <div className="container">
          <SectionHeader
            id="videos-title"
            title="Photorealism in the field"
            link={{ to: "/3d-visualization", label: "3D Visualization" }}
          />
          <VideoReel videos={GRID_VIDEOS} label="Photorealistic visualization videos" />
        </div>
      </section>

      <CTABand
        variant="statement"
        image="/images/site/123bim/photo-morning"
        title="Commission photorealistic visuals"
        text="Send a design or an existing scan and get raytraced stills and video back for your next review."
        secondary={{ to: "/contact", label: "Talk to our team" }}
      />
    </>
  );
}
